"use client";

import { useTERouter } from "@/hooks/app/useTERouter";
import { ESIM_POPULAR_COUNTRY_CODES } from "@/constants/esim/esimPopularCountries";
import { useAppDispatch } from "@/lib/redux/hooks";
import { thunks } from "@/lib/redux/thunks";
import { EsimPackage } from "@/lib/redux/features/esim/types";
import { buildEsimCatalogSearchBody } from "@/utils/esim/buildEsimSearchBody";
import {
  buildEsimPackageOptions,
  EsimPackageOption,
  getEsimPackageNavigationUrl,
} from "@/utils/esim/esimPackageAutoCompleteOptions";
import { AutoComplete, Spin } from "antd";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

type OptionGroup = { label: ReactNode; options: EsimPackageOption[] };

const renderGroupLabel = (text: string): ReactNode => (
  <span className="esim-hub__search-group-label">{text}</span>
);

type SelectCountryProps = {
  /** Pre-fill when landing on the results page with an existing `search` query in the URL. */
  initialQuery?: string;
  /** Lets the sibling "Tìm" button (or Enter key) know what's currently typed, to submit to the full results page. */
  onQueryChange?: (value: string) => void;
  onPressEnter?: () => void;
};

const SelectCountry: FC<SelectCountryProps> = ({ initialQuery, onQueryChange, onPressEnter }) => {
  const dispatch = useAppDispatch();
  const router = useTERouter();
  const { t, i18n } = useTranslation();
  const [text, setText] = useState(initialQuery ?? "");
  const [results, setResults] = useState<EsimPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ abort: () => void } | null>(null);
  const requestIdRef = useRef(0);

  const runSearch = (value: string, opts?: { allowEmpty?: boolean }) => {
    pendingRef.current?.abort();
    const trimmed = value.trim();
    const requestId = ++requestIdRef.current;

    if (trimmed.length < MIN_SEARCH_LENGTH && !opts?.allowEmpty) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const request = dispatch(
      thunks.esim_catalog_public_list({
        body: {
          TYPE: "JSON",
          ...(trimmed
            ? buildEsimCatalogSearchBody(trimmed)
            : { countriesAny: [...ESIM_POPULAR_COUNTRY_CODES], page: 1, limit: 20 }),
        },
      }),
    );
    pendingRef.current = request;
    request
      .unwrap()
      .then((payload) => {
        if (requestIdRef.current !== requestId) return;
        setResults(payload?.esims ?? []);
        setLoading(false);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      });
  };

  // Gọi ngay khi mount để dropdown có sẵn option mặc định (trước đây phải gõ ≥2 ký tự mới có gợi ý) —
  // chưa có `initialQuery` thì lấy theo 16 quốc gia phổ biến (`countriesAny`, OR-match).
  useEffect(() => {
    runSearch(text, { allowEmpty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const packageOptions = useMemo(
    () => buildEsimPackageOptions(results, i18n.language),
    [results, i18n.language],
  );

  const options: OptionGroup[] = packageOptions.length
    ? [{ label: renderGroupLabel(t("esim.search_results_title")), options: packageOptions }]
    : [];

  return (
    <AutoComplete
      className="esim-hub__search-select"
      style={{ width: "100%" }}
      size="large"
      allowClear
      value={text}
      options={options}
      notFoundContent={loading ? <Spin size="small" /> : undefined}
      placeholder={t("esim.search_country_label")}
      onChange={(value) => {
        setText(value);
        onQueryChange?.(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(value || ""), SEARCH_DEBOUNCE_MS);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") onPressEnter?.();
      }}
      onSelect={(_value, option) => {
        const { pkg } = option as unknown as EsimPackageOption;
        const url = getEsimPackageNavigationUrl(pkg);
        if (url) router.push(url);
      }}
    />
  );
};

export default SelectCountry;
